import React, { useState, useEffect, useCallback } from 'react';
import { getWorkspaceProjects, moveCardToProject } from '../utils/firebaseUtils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useLocalStorage } from '../hooks/useLocalStorage';
import {
    Overlay,
    Content,
    Header,
    CloseBtn,
    Label,
    StyledSelect,
    ButtonContainer,
    StyledButton,
    ErrorMessage,
    LoadingMessage
} from '../styles/MoveCardModal.styles';

const MoveCardModal = ({
    cardData,
    currentProjectId,
    currentColumnId,
    currentColumnTitle, // Added new prop
    currentWorkspaceId,
    onClose,
    onCardMoved,
}) => {
    const [cachedProjects, setCachedProjects, clearCachedProjects] = useLocalStorage('moveCard_cachedProjects', []);
    const [lastSelectedProjectId, setLastSelectedProjectId] = useLocalStorage('moveCard_lastSelectedProjectId', '');
    const [projects, setProjects] = useState(cachedProjects || []);
    const [selectedProjectId, setSelectedProjectId] = useState(lastSelectedProjectId || '');
    const [targetColumns, setTargetColumns] = useState([]);
    const [selectedColumnId, setSelectedColumnId] = useState('');
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [isLoadingColumns, setIsLoadingColumns] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!currentWorkspaceId || currentWorkspaceId === "placeholder_workspace_id") {
            setError('Workspace ID is missing. Cannot load projects.');
            console.error('MoveCardModal: Workspace ID is missing.');
            return;
        }
        setError('');
        // Use cached projects if available, but still fetch in background
        if (cachedProjects && cachedProjects.length > 0) {
            setProjects(cachedProjects.filter(p => p.id !== currentProjectId));
        } else {
            setIsLoadingProjects(true);
        }

        getWorkspaceProjects(currentWorkspaceId)
            .then(fetchedProjects => {
                const filteredProjects = fetchedProjects.filter(p => p.id !== currentProjectId);
                setProjects(filteredProjects);
                setCachedProjects(filteredProjects); // Update cache

                // Validate lastSelectedProjectId against the new list of available projects
                if (lastSelectedProjectId && !filteredProjects.find(p => p.id === lastSelectedProjectId)) {
                    setSelectedProjectId(''); // Reset if last selected project is not available
                    // Optionally, clear lastSelectedProjectId from local storage if it's invalid,
                    // or leave it so if it becomes available again it can be pre-filled.
                    // For now, just resetting the current selection.
                }
                setIsLoadingProjects(false);
            })
            .catch(err => {
                console.error("Error fetching workspace projects:", err);
                if (!cachedProjects || cachedProjects.length === 0) { // Only show error if no cache
                    setError('Failed to load projects.');
                }
                setIsLoadingProjects(false);
            });
    }, [currentWorkspaceId, currentProjectId, cachedProjects, setCachedProjects]);

    const fetchColumnsForProject = useCallback(async (projectId) => {
        if (!projectId) {
            setTargetColumns([]);
            setSelectedColumnId('');
            return;
        }
        setIsLoadingColumns(true);
        setError('');
        try {
            const projectRef = doc(db, 'projects', projectId);
            const projectSnap = await getDoc(projectRef);
            if (projectSnap.exists()) {
                const projectData = projectSnap.data();
                const columnsArray = projectData.columnOrder.map(colId => ({
                    id: colId,
                    title: projectData.columns[colId].title,
                }));
                setTargetColumns(columnsArray);
                if (columnsArray.length > 0) {
                    let matchingColumn = null;
                    if (currentColumnTitle) { // Use the passed prop
                        matchingColumn = columnsArray.find(
                            col => col.title.toLowerCase() === currentColumnTitle.toLowerCase()
                        );
                    }

                    if (matchingColumn) {
                        setSelectedColumnId(matchingColumn.id);
                    } else {
                        setSelectedColumnId(columnsArray[0].id); // Default to first column if no match or no currentColumnTitle
                    }
                } else {
                    setSelectedColumnId('');
                }
            } else {
                setError('Selected project data not found.');
                setTargetColumns([]);
            }
        } catch (err) {
            console.error("Error fetching columns for target project:", err);
            setError('Failed to load columns for the selected project.');
            setTargetColumns([]);
        } finally {
            setIsLoadingColumns(false);
        }
    }, []);

    useEffect(() => {
        if (selectedProjectId) {
            fetchColumnsForProject(selectedProjectId);
        } else {
            setTargetColumns([]);
            setSelectedColumnId('');
        }
    }, [selectedProjectId, fetchColumnsForProject]);

    const handleMoveCard = async () => {
        if (!selectedProjectId || !selectedColumnId) {
            setError('Please select a target project and column.');
            return;
        }
        setError('');
        try {
            await moveCardToProject(
                cardData,
                currentProjectId,
                currentColumnId,
                selectedProjectId,
                selectedColumnId
            );
            setLastSelectedProjectId(selectedProjectId); // Store the selected project ID
            onCardMoved(); // Callback to inform parent
            onClose(); // Close this modal
        } catch (err) {
            console.error("Error moving card:", err);
            setError(`Failed to move card: ${err.message}`);
        }
    };

    return (
        <Overlay onClick={onClose}>
            <Content onClick={e => e.stopPropagation()}>
                <CloseBtn onClick={onClose}>&times;</CloseBtn>
                <Header>Move Card</Header>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <div>
                    <Label htmlFor="project-select">Select Target Project:</Label>
                    {isLoadingProjects ? (
                        <LoadingMessage>Loading projects...</LoadingMessage>
                    ) : (
                        <StyledSelect
                            id="project-select"
                            value={selectedProjectId}
                            onChange={e => setSelectedProjectId(e.target.value)}
                            disabled={projects.length === 0 && !isLoadingProjects}
                        >
                            <option value="">
                                {projects.length === 0
                                    ? "No other projects in this workspace"
                                    : "Select a project"}
                            </option>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.title}</option>
                            ))}
                        </StyledSelect>
                    )}
                </div>

                {selectedProjectId && (
                    <div>
                        <Label htmlFor="column-select">Select Target Column/Status:</Label>
                        {isLoadingColumns ? (
                            <LoadingMessage>Loading columns...</LoadingMessage>
                        ) : (
                            <StyledSelect
                                id="column-select"
                                value={selectedColumnId}
                                onChange={e => setSelectedColumnId(e.target.value)}
                                disabled={targetColumns.length === 0 && !isLoadingColumns}
                            >
                                <option value="">
                                    {targetColumns.length === 0
                                        ? "No columns in this project"
                                        : "Select a column"}
                                </option>
                                {targetColumns.map(col => (
                                    <option key={col.id} value={col.id}>{col.title}</option>
                                ))}
                            </StyledSelect>
                        )}
                    </div>
                )}
                <ButtonContainer>
                    <StyledButton
                        secondary
                        onClick={onClose}
                    >
                        Cancel
                    </StyledButton>
                    <StyledButton
                        primary
                        onClick={handleMoveCard}
                        disabled={!selectedProjectId || !selectedColumnId || isLoadingProjects || isLoadingColumns}
                    >
                        Move Card
                    </StyledButton>
                </ButtonContainer>
            </Content>
        </Overlay>
    );
};

export default MoveCardModal;
